// ============================================================
// The workshop folder, from inside a session.
//
// Characters have lived in three unconnected places: localStorage, a
// Firestore collection scoped to ONE session, and the Workshop's folder
// on disk. Only the folder survives a session ending, and only the
// folder has any structure - and it had no way whatsoever to reach a
// live table (v8.15.0).
//
// So this is the folder, browsed in place. It READS; it does not write.
// Authoring stays in the Workshop, which is the one thing that should be
// editing files.
//
// AND READING IS ALL A DIRECTORY INPUT NEEDS. This was built on
// `showDirectoryPicker`, which buys write access nobody here wants and
// charges a secure context for it - so over a dev server reached across
// a tailnet it did not exist, and the view spent its life telling a
// Chrome user to go and get Chrome. `<input webkitdirectory>` hands over
// every file under a chosen folder, with its path, in every browser and
// on plain HTTP (v8.23.1).
//
// One thing the browser still imposes, stated rather than fought: the
// folder is chosen by a real click and nothing about it is remembered,
// so it is chosen once per visit.
// ============================================================
import { useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import CharacterShelf, { charPath } from './workshop/CharacterShelf';
import { readCharactersFromFiles } from '../utils/folderImport';
import { treeFromFileList, allFileCharacters, fileCharactersAt, labelsIn, asShelfTree } from '../utils/folderFiles';
import type { FileCharacter, FileTree } from '../utils/folderFiles';
import type { Character } from '../engine/character/types';
import { DIRECTORY_INPUT } from '../utils/directoryInput';
import { folderImportNote, labelledVersion, resolveVersions, stagedWhere } from './characterFolderImport';
import type { FolderImportMode } from './characterFolderImport';
import common from '../styles/common.module.css';
import styles from '../styles/Workshop.module.css';

interface WaitingRoomChoice {
  id: string;
  name: string;
}

export interface CharacterFolderViewProps {
  /** Paths currently picked, as `Group/Name` or `Name`. */
  selected: Set<string>;
  onToggleSelect: (path: string, e: React.MouseEvent, remove?: boolean) => void;
  /** Clicking a group name: all of them, or none. */
  onSelectGroup?: (paths: string[]) => void;
  /** The draw order, reported up so a shift-range walks what is on screen. */
  onOrder?: (paths: string[]) => void;
  onClearSelection?: () => void;
  /** The folder, held above this component so it survives the panel closing. */
  files?: File[] | null;
  onFiles?: (files: File[]) => void;
  /** Given everything read, in folder order. */
  onImport: (characters: Character[]) => void;
  /** Stage them in a waiting room instead. Absent for anyone without the grant. */
  /** Returns the room's name (it may have just made it), or null if nothing happened. */
  onStage?: (characters: Character[], roomId?: string) => string | null | void;
  /** Put them straight onto the roster and the map, skipping the library. */
  onLoad?: (characters: Character[]) => void;
  /** The rooms that exist, so staging can name one instead of guessing. */
  rooms?: WaitingRoomChoice[];
  /** Open the panel's own single-file picker. */
  onImportFile?: () => void;
}

// `asShelfTree` and `FileShelfEntry` live in folderFiles.ts since
// v8.84.0 - the maps view renders the same shelf from the same adapter,
// and ten duplicated lines is where the two would have diverged.

type Pins = Record<string, string>;

/** The folder's own choices: which build, which room, and who breaks ranks. */
function useFolderChoices() {
  const [roomId, setRoomId] = useState<string>('');
  // The build to read everyone at - see `pickByLabel`. Empty means
  // "everyone at their latest", which is what it always did.
  const [label, setLabel] = useState('');
  // Per-character overrides, for the one who needs a different version
  // from the rest of the squad. Keyed by shelf path.
  const [pinned, setPinned] = useState<Pins>({});
  return { roomId, setRoomId, label, setLabel, pinned, setPinned };
}

type Choices = ReturnType<typeof useFolderChoices>;
type Deliverers = Pick<CharacterFolderViewProps, 'onImport' | 'onStage' | 'onLoad' | 'rooms'>;

/**
 * Who receives a mode's characters. A mode whose receiver is absent falls
 * back to a plain import; staging returns the room it used.
 */
function receiverFor(
  mode: FolderImportMode,
  { onImport, onStage, onLoad }: Deliverers,
  roomId: string,
): (characters: Character[]) => string | null | void {
  if (mode === 'stage' && onStage) return (characters) => onStage(characters, roomId || undefined);
  if (mode === 'load' && onLoad) return onLoad;
  return onImport;
}

/**
 * Read a set of entries and hand them over, then report.
 *
 * The report is the point. A folder of twenty with one bad file used
 * to import nothing and say nothing; now nineteen arrive and the
 * twentieth is named (`folderImportNote`).
 */
function useFolderImport(receivers: Deliverers, choices: Choices) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const { label, pinned, roomId } = choices;
  const importThese = async (entries: FileCharacter[], what: string, mode: FolderImportMode = 'import') => {
    if (!entries.length) return;
    setBusy(true);
    setNote(null);
    try {
      const { resolved, fellBack } = resolveVersions(entries, label, pinned);
      const out = await readCharactersFromFiles(resolved);
      const staged = out.characters.length ? receiverFor(mode, receivers, roomId)(out.characters) : null;
      const where = mode === 'stage' ? stagedWhere(staged, roomId, receivers.rooms ?? []) : '';
      const report = { mode, count: out.characters.length, what, where, fellBack, label };
      setNote(folderImportNote({ ...report, failed: out.failed, warnings: out.warnings.length }));
    } finally {
      setBusy(false);
    }
  };
  return { busy, note, setNote, importThese };
}

/** The hidden directory input: every file under the chosen folder, with its path. */
function FolderPicker({
  inputRef,
  onPick,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  onPick: (files: FileList | null) => void;
}) {
  return (
    <input
      ref={inputRef}
      type="file"
      // Not in the JSX type, and the two spellings are what browsers
      // actually read. Both are set; either alone leaves somebody out.
      {...DIRECTORY_INPUT}
      multiple
      style={{ display: 'none' }}
      data-testid="folder-input"
      onChange={(e) => onPick(e.target.files)}
    />
  );
}

/**
 * TWO BUTTONS, NO ESSAY. The paragraph explaining that nothing is
 * written and that browsers cannot remember a folder was true, and
 * nobody needed it twice; what is left of it says the same in a title
 * (v8.25.0).
 */
function FolderStart({
  picker,
  onOpen,
  onImportFile,
}: {
  picker: ReactNode;
  onOpen: () => void;
  onImportFile?: () => void;
}) {
  return (
    <div className={styles.folderStart} data-testid="folder-closed">
      {picker}
      <button
        className={common.btnSmall}
        data-testid="folder-open"
        title="The same folder the Workshop uses. Nothing is copied and nothing is written - this reads it."
        onClick={onOpen}
      >
        Choose folder…
      </button>
      {onImportFile && (
        <button
          className={common.btnSmall}
          data-testid="folder-import-file"
          title="Read a single character from a JSON file"
          onClick={onImportFile}
        >
          Import a file…
        </button>
      )}
    </div>
  );
}

/** The count a send button carries: the selection's size, or "all" when nothing is picked. */
const sendCount = (count: number) => (count ? ` (${count})` : ' (all)');

interface StageProps {
  roomId: string;
  onRoom: (id: string) => void;
  rooms: WaitingRoomChoice[];
  disabled: boolean;
  count: number;
  onStage: () => void;
}

/** The room chooser and the stage button. */
function StageControls({ roomId, onRoom, rooms, disabled, count, onStage }: StageProps) {
  return (
    <>
      {/* WHICH ROOM, rather than whichever happens to be active.
          "Send to the waiting room" with no way to say which one
          is a guess wearing a button (v8.23.1). */}
      {/* ALWAYS THERE, even with no rooms - it is how one gets
          made. Hiding it until a room existed was the dead end. */}
      <select
        className={common.input}
        aria-label="Waiting room"
        data-testid="folder-room"
        value={roomId}
        onChange={(e) => onRoom(e.target.value)}
      >
        <option value="">Active room</option>
        {rooms.map((r) => (
          <option key={r.id} value={r.id}>
            {r.name}
          </option>
        ))}
        {/* A ROOM MADE FROM HERE. Staging into a session that has
            no rooms yet used to do nothing at all (v8.26.0). */}
        <option value="new:">＋ New room…</option>
      </select>
      <button
        className={common.btnSmall}
        data-testid="folder-stage-selected"
        disabled={disabled}
        title="Put them in a waiting room; nobody else will see them"
        onClick={onStage}
      >
        → Waiting{sendCount(count)}
      </button>
    </>
  );
}

/**
 * WHICH BUILD. Type a label - `mission-4` - and everybody comes in at
 * the iteration carrying it, falling back to their latest and saying
 * how many did. The datalist offers the labels the folder actually has
 * rather than making you remember them.
 */
function BuildLabel({
  label,
  onLabel,
  everyone,
}: {
  label: string;
  onLabel: (l: string) => void;
  everyone: FileCharacter[];
}) {
  return (
    <>
      <label className={common.label}>
        Build
        <input
          className={common.input}
          list="folder-labels"
          placeholder="latest"
          aria-label="Build label"
          data-testid="folder-label"
          value={label}
          onChange={(e) => onLabel(e.target.value)}
          style={{ width: 110 }}
        />
      </label>
      <datalist id="folder-labels">
        {labelsIn(everyone).map((l) => (
          <option key={l} value={l} />
        ))}
      </datalist>
    </>
  );
}

interface ToolbarProps extends Pick<
  CharacterFolderViewProps,
  'selected' | 'onLoad' | 'onStage' | 'onClearSelection' | 'rooms'
> {
  tree: FileTree;
  folderName: string | null;
  choices: Choices;
  busy: boolean;
  importThese: ReturnType<typeof useFolderImport>['importThese'];
  picker: ReactNode;
  onRechoose: () => void;
}

/** Import all, and import the selection. */
function ImportButtons({ tree, folderName, selected, busy, importThese }: ToolbarProps) {
  const everyone = allFileCharacters(tree);
  const count = selected.size;
  return (
    <>
      <button
        className={common.btnSmall}
        data-testid="folder-import-all"
        disabled={busy || everyone.length === 0}
        onClick={() => importThese(everyone, folderName ?? 'the folder')}
      >
        Import all ({everyone.length})
      </button>
      <button
        className={common.btnSmall}
        data-testid="folder-import-selected"
        disabled={busy || count === 0}
        onClick={() => importThese(fileCharactersAt(tree, selected), 'the selection')}
      >
        Import selected{count > 1 ? ` (${count})` : ''}
      </button>
    </>
  );
}

/** Load and stage: the selection, or everyone when nothing is picked - bulk is the point. */
function SendButtons({ tree, selected, busy, importThese, onLoad, onStage, rooms = [], choices }: ToolbarProps) {
  const everyone = allFileCharacters(tree);
  const count = selected.size;
  const disabled = busy || (count === 0 && everyone.length === 0);
  const pickedOrAll = () => (count ? fileCharactersAt(tree, selected) : everyone);
  return (
    <>
      {onLoad && (
        <button
          className={common.btnSmall}
          data-testid="folder-load-selected"
          disabled={disabled}
          title="Straight onto the roster and the map"
          onClick={() => importThese(pickedOrAll(), 'the selection', 'load')}
        >
          Load{sendCount(count)}
        </button>
      )}
      {onStage && (
        <StageControls
          roomId={choices.roomId}
          onRoom={choices.setRoomId}
          rooms={rooms}
          disabled={disabled}
          count={count}
          onStage={() => importThese(pickedOrAll(), 'the selection', 'stage')}
        />
      )}
    </>
  );
}

/** The bar over the tree: import, load, stage, the build label, clear, change folder. */
function FolderToolbar(props: ToolbarProps) {
  const { tree, selected, choices, onClearSelection, picker, onRechoose } = props;
  return (
    <div className={styles.charHeaderActions}>
      {picker}
      <ImportButtons {...props} />
      <SendButtons {...props} />
      <BuildLabel label={choices.label} onLabel={choices.setLabel} everyone={allFileCharacters(tree)} />
      {selected.size > 0 && onClearSelection && (
        <button className={common.btnSmall} data-testid="folder-clear" onClick={onClearSelection}>
          Clear ({selected.size})
        </button>
      )}
      <button className={common.btnSmall} data-testid="folder-rechoose" onClick={onRechoose}>
        Change folder
      </button>
    </div>
  );
}

/**
 * ONE CHARACTER CAN BREAK RANKS. The rest of the squad comes at the
 * label; this one comes at whatever you say. Only a picker when there is
 * more than one version to choose between.
 */
function IterationPicker({ character: c, choices }: { character: FileCharacter; choices: Choices }) {
  const { label, pinned, setPinned } = choices;
  const path = charPath(c.group, c.name);
  const chosen = pinned[path] ?? labelledVersion(c, label);
  if (c.iterations.length < 2) return <span className={styles.iterationOne}>{chosen}</span>;
  const pin = (value: string) =>
    setPinned((prev) => {
      const next = { ...prev };
      // Choosing what the label already gives you is not a pin - it is
      // agreement, and it must not outlive a change of label.
      if (value === labelledVersion(c, label)) delete next[path];
      else next[path] = value;
      return next;
    });
  return (
    <select
      className={common.input}
      aria-label={`Iteration for ${c.name}`}
      data-testid={`folder-iteration-${path}`}
      value={chosen}
      onChange={(e) => pin(e.target.value)}
    >
      {c.iterations.map((f) => (
        <option key={f.name} value={f.name}>
          {f.name}
        </option>
      ))}
    </select>
  );
}

/** The tree's collapsed folders. */
function useCollapsed() {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggleGroup = (name: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  return { collapsed, toggleGroup };
}

/**
 * THE FOLDER LIVES ABOVE THIS COMPONENT. The panel unmounts every time
 * it loses focus, and with the tree in local state that meant choosing
 * the folder again on every visit (v8.24.0). The `File` objects stay
 * valid for the life of the page, so holding them up there is all it
 * takes - a reload still needs a fresh pick, because the browser gives
 * no way to remember a directory without the picker API.
 */
function useFolderTree(files: File[] | null) {
  const tree = useMemo(() => (files?.length ? treeFromFileList(files) : null), [files]);
  const folderName = useMemo(() => {
    const first = files?.[0] as (File & { webkitRelativePath?: string }) | undefined;
    return first?.webkitRelativePath?.split('/')[0] ?? null;
  }, [files]);
  return { tree, folderName };
}

export default function CharacterFolderView(props: CharacterFolderViewProps) {
  const { selected, onToggleSelect, onSelectGroup, onOrder, onImportFile, files = null, onFiles } = props;
  const dirRef = useRef<HTMLInputElement>(null);
  const { tree, folderName } = useFolderTree(files);
  const { collapsed, toggleGroup } = useCollapsed();
  const choices = useFolderChoices();
  const { busy, note, setNote, importThese } = useFolderImport(props, choices);
  const readFolder = (picked: FileList | null) => {
    if (!picked || picked.length === 0) return;
    onFiles?.(Array.from(picked));
    setNote(null);
  };
  const choose = () => dirRef.current?.click();
  const picker = <FolderPicker inputRef={dirRef} onPick={readFolder} />;
  if (!tree) return <FolderStart picker={picker} onOpen={choose} onImportFile={onImportFile} />;
  const empty = allFileCharacters(tree).length === 0;
  return (
    <div data-testid="folder-view">
      <FolderToolbar {...props} {...{ tree, folderName, choices, busy, importThese, picker }} onRechoose={choose} />
      <p className={styles.hint} data-testid="folder-grammar">
        Click to pick · right-click to drop · shift+click for a range · ⛶ takes a whole folder
      </p>
      {note && (
        <p className={styles.notice} data-testid="folder-import-note" role="status" aria-live="polite">
          {note}
        </p>
      )}
      {busy && <p className={styles.hint}>Reading…</p>}
      {empty && <p className={styles.hint}>No character files in that folder.</p>}
      <CharacterShelf
        tree={asShelfTree(tree)}
        collapsed={collapsed}
        onToggleGroup={toggleGroup}
        groupActions={(group) => (
          <button
            className={common.btnSmall}
            data-testid={`folder-import-group-${group.name}`}
            disabled={busy || group.characters.length === 0}
            title={`Import everyone in ${group.name}`}
            onClick={() => importThese(tree.groups.find((g) => g.name === group.name)?.characters ?? [], group.name)}
          >
            ⤓ {group.characters.length}
          </button>
        )}
        characterActions={(dir) => <IterationPicker character={dir.entry} choices={choices} />}
        selected={selected}
        onToggleSelect={onToggleSelect}
        onSelectGroup={onSelectGroup}
        onOrder={onOrder}
      />
    </div>
  );
}
